import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Clock, Trophy, RotateCcw } from 'lucide-react';
import { QuizQuestion } from '../../lib/gemini';
import { useAuth } from '../../contexts/AuthContext';

interface QuizModeProps {
  questions: QuizQuestion[];
  onComplete?: (score: number, results: QuizResult[]) => void;
}

interface QuizResult {
  question: string;
  selectedAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  explanation: string;
}

export const QuizMode: React.FC<QuizModeProps> = ({ questions, onComplete }) => {
  const { updateStudyStats, userProfile } = useAuth();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [showResult, setShowResult] = useState(false);
  const [results, setResults] = useState<QuizResult[]>([]);
  const [timeLeft, setTimeLeft] = useState(30); // 30 seconds per question
  const [isTimerActive, setIsTimerActive] = useState(true);
  const [quizCompleted, setQuizCompleted] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  // Timer logic
  useEffect(() => {
    if (isTimerActive && timeLeft > 0 && !showResult) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !showResult) {
      // Time's up, auto-submit
      handleSubmitAnswer();
    }
  }, [timeLeft, isTimerActive, showResult]);

  const handleAnswerSelect = (answer: string) => {
    if (showResult) return;
    setSelectedAnswer(answer);
  };

  const handleSubmitAnswer = () => {
    if (showResult) return;

    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    const newResult: QuizResult = {
      question: currentQuestion.question,
      selectedAnswer: selectedAnswer || 'No answer selected',
      correctAnswer: currentQuestion.correctAnswer,
      isCorrect,
      explanation: currentQuestion.explanation
    };

    setResults([...results, newResult]);
    setShowResult(true);
    setIsTimerActive(false);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer('');
      setShowResult(false);
      setTimeLeft(30);
      setIsTimerActive(true);
    } else {
      // Quiz completed
      setQuizCompleted(true);
      const score = Math.round((results.filter(r => r.isCorrect).length / questions.length) * 100);
      if (onComplete) {
        onComplete(score, results);
      }
      // Store quiz session metadata in localStorage
      const quizMeta = {
        score,
        correctAnswers: results.filter(r => r.isCorrect).length,
        totalQuestions: questions.length,
        completedAt: new Date().toISOString(),
      };
      const existingQuizzes = JSON.parse(localStorage.getItem('ecstatic_uploaded_quizzes') || '[]');
      existingQuizzes.unshift(quizMeta);
      localStorage.setItem('ecstatic_uploaded_quizzes', JSON.stringify(existingQuizzes));
      // Increment quizzesCompleted in userProfile.studyStats
      if (userProfile) {
        updateStudyStats({ quizzesCompleted: (userProfile.studyStats.quizzesCompleted || 0) + 1 });
      }
    }
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswer('');
    setShowResult(false);
    setResults([]);
    setTimeLeft(30);
    setIsTimerActive(true);
    setQuizCompleted(false);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-success-600';
    if (score >= 60) return 'text-warning-600';
    return 'text-error-600';
  };

  const getScoreMessage = (score: number) => {
    if (score >= 90) return 'Excellent work! 🎉';
    if (score >= 80) return 'Great job! 👏';
    if (score >= 70) return 'Good effort! 👍';
    if (score >= 60) return 'Keep practicing! 💪';
    return 'Review the material and try again! 📚';
  };

  if (quizCompleted) {
    const score = Math.round((results.filter(r => r.isCorrect).length / questions.length) * 100);
    const correctAnswers = results.filter(r => r.isCorrect).length;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto space-y-6"
      >
        {/* Results Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center border border-gray-200 dark:border-gray-700">
          <div className="mb-4">
            <Trophy className="h-12 w-12 text-primary-600 mx-auto mb-2" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Quiz Complete!
            </h2>
          </div>
          
          <div className="space-y-4">
            <div className={`text-4xl font-bold ${getScoreColor(score)}`}>
              {score}%
            </div>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {correctAnswers} out of {questions.length} correct
            </p>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {getScoreMessage(score)}
            </p>
          </div>
          
          <button
            onClick={resetQuiz}
            className="mt-6 flex items-center space-x-2 mx-auto px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Try Again</span>
          </button>
        </div>

        {/* Detailed Results */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Review Your Answers
          </h3>
          {results.map((result, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-start space-x-3">
                {result.isCorrect ? (
                  <CheckCircle className="h-5 w-5 text-success-600 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-error-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white mb-2">
                    {result.question}
                  </p>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-600 dark:text-gray-400">
                      Your answer: <span className={result.isCorrect ? 'text-success-600' : 'text-error-600'}>
                        {result.selectedAnswer}
                      </span>
                    </p>
                    {!result.isCorrect && (
                      <p className="text-gray-600 dark:text-gray-400">
                        Correct answer: <span className="text-success-600">{result.correctAnswer}</span>
                      </p>
                    )}
                    <p className="text-gray-500 dark:text-gray-400 italic">
                      {result.explanation}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  if (!questions.length) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">No quiz questions available</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Progress and Timer */}
      <div className="flex justify-between items-center">
        <div className="flex-1 mr-4">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <motion.div
              className="bg-primary-600 h-2 rounded-full"
              style={{ width: `${progress}%` }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Question {currentQuestionIndex + 1} of {questions.length}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Clock className="h-4 w-4 text-gray-500" />
          <span className={`text-sm font-medium ${
            timeLeft <= 10 ? 'text-error-600' : 'text-gray-600 dark:text-gray-400'
          }`}>
            {timeLeft}s
          </span>
        </div>
      </div>

      {/* Question */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          {currentQuestion.question}
        </h2>

        {/* Answer Options */}
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => (
            <motion.button
              key={index}
              onClick={() => handleAnswerSelect(option)}
              disabled={showResult}
              className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                showResult
                  ? option === currentQuestion.correctAnswer
                    ? 'border-success-500 bg-success-50 dark:bg-success-900/20'
                    : selectedAnswer === option
                    ? 'border-error-500 bg-error-50 dark:bg-error-900/20'
                    : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50'
                  : selectedAnswer === option
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-600 hover:border-primary-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
              whileHover={{ scale: showResult ? 1 : 1.02 }}
              whileTap={{ scale: showResult ? 1 : 0.98 }}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded-full border-2 ${
                  showResult
                    ? option === currentQuestion.correctAnswer
                      ? 'border-success-500 bg-success-500'
                      : selectedAnswer === option
                      ? 'border-error-500 bg-error-500'
                      : 'border-gray-300 dark:border-gray-600'
                    : selectedAnswer === option
                    ? 'border-primary-500 bg-primary-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {((showResult && option === currentQuestion.correctAnswer) || 
                    (!showResult && selectedAnswer === option)) && (
                    <div className="w-2 h-2 rounded-full bg-white m-0.5" />
                  )}
                </div>
                <span className="text-gray-900 dark:text-white">{option}</span>
                {showResult && option === currentQuestion.correctAnswer && (
                  <CheckCircle className="h-4 w-4 text-success-600 ml-auto" />
                )}
                {showResult && selectedAnswer === option && option !== currentQuestion.correctAnswer && (
                  <XCircle className="h-4 w-4 text-error-600 ml-auto" />
                )}
              </div>
            </motion.button>
          ))}
        </div>

        {/* Explanation */}
        <AnimatePresence>
          {showResult && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
            >
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Explanation:</h4>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {currentQuestion.explanation}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Submit/Next Button */}
        <div className="mt-6 flex justify-end">
          {showResult ? (
            <button
              onClick={handleNextQuestion}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              {currentQuestionIndex === questions.length - 1 ? 'Complete Quiz' : 'Next Question'}
            </button>
          ) : (
            <button
              onClick={handleSubmitAnswer}
              disabled={!selectedAnswer}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Submit Answer
            </button>
          )}
        </div>
      </div>
    </div>
  );
};